import { type dash_growth } from "@dropbox/api-v2-client";
import { callApiV2 } from "@mirage/service-dbx-api";
import { useCallback, useRef } from "react";
import { EmailValidationResult, type TeamMember } from "../types";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type EmailValidator = (
  email: string,
) => Promise<EmailValidationResult | null>;

interface UseInviteFormEmailValidationArgs {
  knownValidTeamMemberInvitees: Readonly<TeamMember[]>;
}

interface UseInviteFormEmailValidationReturnType {
  emailValidator: EmailValidator;
  emailValidatorEmptyCache: () => void;
}

export const useInviteFormEmailValidation = ({
  knownValidTeamMemberInvitees,
}: UseInviteFormEmailValidationArgs): UseInviteFormEmailValidationReturnType => {
  const errorCacheRef = useRef<Record<string, EmailValidationResult | null>>(
    {},
  );

  const emailValidatorEmptyCache = useCallback(() => {
    errorCacheRef.current = {};
  }, []);

  const emailValidator: EmailValidator = useCallback(
    async (email) => {
      let validationResult: EmailValidationResult | null = null;
      if (!EMAIL_REGEX.test(email)) {
        // TODO: replace with existing email validation util if available
        validationResult = EmailValidationResult.MALFORMED;
      } else if (errorCacheRef.current[email] !== undefined) {
        validationResult = errorCacheRef.current[email];
      } else if (
        // Existing team members are automatically marked as valid
        // Do not need to validate through API endpoint
        // Form will not display any error or info message
        knownValidTeamMemberInvitees.some((member) => member.email === email)
      ) {
        validationResult =
          EmailValidationResult.VALID_EMAIL_TEAM_MEMBER_WITHOUT_DASH;
      } else {
        try {
          const { result } = await callApiV2(
            "dashGrowthTeamInvitesValidateInviteeEmails",
            {
              emails: [email],
            },
          );
          const reason: dash_growth.InvalidEmailReason | null = result
            ? result[email]
            : null;
          switch (reason && reason[".tag"]) {
            case "invalid_email_reason_none":
              // Greenfield email should have info message
              validationResult = EmailValidationResult.VALID_EMAIL;
              break;
            case "invalid_email_reason_none_work_user_from_different_team":
              validationResult =
                EmailValidationResult.VALID_EMAIL_WORK_USER_FROM_DIFFERENT_TEAM;
              break;
            case "invalid_email_reason_ineligible_account":
              validationResult =
                EmailValidationResult.INELIGIBLE_FOR_DASH_UNSPECIFIED;
              break;
            case "invalid_email_reason_malformed_email":
              validationResult = EmailValidationResult.MALFORMED;
              break;
            case "invalid_email_reason_existing_team_member_with_dash":
              validationResult =
                EmailValidationResult.TEAM_MEMBER_ALREADY_WITH_DASH;
              break;
            case "invalid_email_reason_pending_team_member":
              validationResult =
                EmailValidationResult.TEAM_MEMBER_PENDING_INVITE;
              break;
            case "invalid_email_reason_associated_with_different_team":
              validationResult =
                EmailValidationResult.ASSOCIATED_WITH_DIFFERENT_TEAM;
              break;
            case "invalid_email_reason_paid_personal_or_trial":
              validationResult = EmailValidationResult.PAID_PERSONAL_OR_TRIAL;
              break;
            case "invalid_email_reason_no_invite_permission":
              validationResult = EmailValidationResult.NO_INVITE_PERMISSION;
              break;
            case "invalid_email_reason_pending_invite":
              validationResult = EmailValidationResult.PENDING_INVITE;
              break;
            case "invalid_email_reason_already_on_another_team":
              validationResult = EmailValidationResult.ALREADY_ON_ANOTHER_TEAM;
              break;
            case "invalid_email_reason_apple_relay_email":
              validationResult = EmailValidationResult.APPLE_RELAY_EMAIL;
              break;
            case "invalid_email_reason_recoverable_user":
              validationResult = EmailValidationResult.RECOVERABLE_USER;
              break;
            default:
              validationResult = EmailValidationResult.UNKNOWN;
          }
        } catch (e) {
          // Deals with transient errors (e.g. network issues)
          // We do not cache the error result in the error cache. This allows API call
          // to be retried the next time the same email is entered/requires validation
          validationResult = EmailValidationResult.VALIDATION_ERROR;
        }
      }

      if (validationResult !== EmailValidationResult.VALIDATION_ERROR) {
        errorCacheRef.current[email] = validationResult;
      }
      return validationResult;
    },
    [knownValidTeamMemberInvitees],
  );

  return { emailValidator, emailValidatorEmptyCache };
};
